import React, { useState, useEffect } from 'react'
import {
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { useHistory } from 'react-router-dom'
import { listDefaultOverlays } from '../../Apis/defaultOverlays'
import { labelFor } from '../../utils/constants/overlayTypes'

export default function DefaultOverlaysListPage() {
  const history = useHistory()
  const [overlays, setOverlays] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    listDefaultOverlays()
      .then((data) => {
        if (cancelled) return
        setOverlays(Array.isArray(data) ? data : [])
        setError(null)
      })
      .catch((err) => {
        if (cancelled) return
        // 404 means the backend isn't running in Development mode (or the
        // editor is disabled via DefaultOverlays:EditorEnabled). Surface a
        // clear message instead of a raw error.
        const status = err?.response?.status
        if (status === 404) {
          setError(
            'Default-overlay editor is unavailable. The backend must be ' +
              'running in Development with DefaultOverlays:EditorEnabled.',
          )
        } else {
          setError(
            err?.response?.data?.message ||
              err?.message ||
              'Failed to load default overlays',
          )
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div style={{ padding: 24 }}>
      <Typography variant='h5' gutterBottom>
        Default overlays
      </Typography>
      <Typography
        variant='body2'
        color='textSecondary'
        style={{ marginBottom: 24 }}
      >
        Edit the bundled SVG + placements for each overlay type. Changes write
        back to the local repo
        (<code>Mismatch.Domain/Overlays/Defaults/</code>). Available in
        Development only.
      </Typography>

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <CircularProgress />
        </div>
      )}

      {!loading && error && (
        <Alert severity='error' style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <Grid container spacing={2}>
          {overlays.map((o) => {
            const label = labelFor(o.type)
            const variants = o.availableVariants ?? []
            const sports = o.availableSports ?? []
            const statsModes = o.availableStatsModes ?? []
            return (
              <Grid item xs={12} sm={6} md={4} key={o.slug}>
                <Card>
                  <CardContent>
                    <Typography variant='h6'>{label}</Typography>
                    <Typography variant='caption' color='textSecondary'>
                      {o.slug}
                    </Typography>
                    {variants.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <Chip
                          size='small'
                          label={`${variants.length} variants`}
                        />
                      </div>
                    )}
                  </CardContent>
                  <CardActions>
                    {/* A type with a board of its own per sport or per stats mode gets one
                        action each: the scoreboard splits by sport, Team Comparison by stats
                        mode. No type splits both ways, so the branches stay independent. */}
                    {sports.length > 1 ? (
                      sports.map((sp) => (
                        <Button
                          key={sp}
                          size='small'
                          color='primary'
                          onClick={() =>
                            history.push(`/default-overlays/${o.slug}?sport=${sp}`)
                          }
                        >
                          {`Edit ${sp}`}
                        </Button>
                      ))
                    ) : statsModes.length > 1 ? (
                      statsModes.map((sm) => (
                        <Button
                          key={sm}
                          size='small'
                          color='primary'
                          onClick={() =>
                            history.push(
                              `/default-overlays/${o.slug}${
                                sm === 'Full' ? '' : `?statsMode=${sm}`
                              }`,
                            )
                          }
                        >
                          {sm === 'Full' ? 'Edit' : 'Edit Lite'}
                        </Button>
                      ))
                    ) : (
                      <Button
                        size='small'
                        color='primary'
                        onClick={() =>
                          history.push(`/default-overlays/${o.slug}`)
                        }
                      >
                        Edit
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            )
          })}
        </Grid>
      )}
    </div>
  )
}
